import { CheckOutlined, CloseOutlined, MinusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { DetailPage } from '../../components/DetailPage';
import { ApiError } from '../../lib/api-client';
import { formatDateTime, orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import { invoiceFromJobCard } from '../pos/api';
import {
  adjustLabour,
  changeJobStatus,
  getJobCard,
  listApprovals,
  recordApproval,
  recordQc,
  type ApprovalChannel,
  type JobCard,
  type JobLine,
  type JobQcItem,
  type JobStatus,
  type QcResult,
} from './api';
import { JobParts } from './JobParts';
import { JobTechnicians } from './JobTechnicians';
import { JOB_STATUS_LABEL, JOB_TYPE_LABEL, QC_RESULT_LABEL, describeSeconds, jobTone } from './status';

const { Text } = Typography;

/** The statuses a service advisor moves a job to by hand, in workflow order. */
const ADVISOR_STATUSES: JobStatus[] = [
  'WAITING_TECHNICIAN',
  'DIAGNOSIS',
  'WAITING_CUSTOMER_APPROVAL',
  'WAITING_PARTS',
  'WORK_IN_PROGRESS',
  'QUALITY_CONTROL',
  'READY_FOR_BILLING',
  'READY_FOR_COLLECTION',
  'COMPLETED',
  'ON_HOLD',
];

const CHANNELS: ApprovalChannel[] = ['IN_PERSON', 'WHATSAPP', 'CALL', 'SMS'];

export function JobCardPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();

  const [moving, setMoving] = useState(false);
  const [nextStatus, setNextStatus] = useState<JobStatus>('WORK_IN_PROGRESS');
  const [mileageOut, setMileageOut] = useState<number | null>(null);
  const [closing, setClosing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [amount, setAmount] = useState('');
  const [channel, setChannel] = useState<ApprovalChannel>('WHATSAPP');
  const [approvedBy, setApprovedBy] = useState('');
  const [adjusting, setAdjusting] = useState<JobLine | null>(null);
  const [hours, setHours] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [noting, setNoting] = useState<{ item: JobQcItem; result: QcResult } | null>(null);
  const [qcNote, setQcNote] = useState('');

  const { data, isLoading } = useQuery({ queryKey: ['job-card', uid], queryFn: () => getJobCard(uid) });
  const { data: approvals } = useQuery({
    queryKey: ['job-approvals', uid],
    queryFn: () => listApprovals(uid),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['job-card', uid] });
    void queryClient.invalidateQueries({ queryKey: ['job-approvals', uid] });
    void queryClient.invalidateQueries({ queryKey: ['job-cards'] });
  };
  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const move = useMutation({
    mutationFn: () =>
      changeJobStatus(uid, {
        status: nextStatus,
        ...(mileageOut !== null ? { mileageOut } : {}),
      }),
    onSuccess: () => {
      refresh();
      setMoving(false);
    },
    // The API's refusal lists exactly what is unresolved. Showing it verbatim
    // is the only reason it bothers to build that list.
    onError: (e: unknown) => {
      fail(e, t('workshop.moveFailed'));
    },
  });

  // The API refuses while money is owed and names the invoice and the balance,
  // which is exactly what the advisor needs to send the customer to the till.
  const close = useMutation({
    mutationFn: () => changeJobStatus(uid, { status: 'CLOSED' }),
    onSuccess: () => {
      refresh();
      setClosing(false);
      void message.success(t('workshop.closed'));
    },
    onError: (e: unknown) => {
      fail(e, t('workshop.closeFailed'));
    },
  });

  const approve = useMutation({
    mutationFn: () =>
      recordApproval(uid, {
        kind: (data?.approvedAmount ?? null) === null ? 'ESTIMATE' : 'ADDITIONAL_WORK',
        amountAfter: amount.trim(),
        channel,
        approvedByName: approvedBy.trim(),
      }),
    onSuccess: () => {
      refresh();
      setApproving(false);
      void message.success(t('workshop.approvalRecorded'));
    },
    onError: (e: unknown) => {
      fail(e, t('workshop.approvalFailed'));
    },
  });

  const adjust = useMutation({
    mutationFn: () =>
      adjustLabour(adjusting?.uid ?? '', { hours: hours.trim(), reason: adjustReason.trim() }),
    onSuccess: () => {
      refresh();
      setAdjusting(null);
    },
    onError: (e: unknown) => {
      fail(e, t('workshop.adjustFailed'));
    },
  });

  /**
   * The handoff to the till. Lives here rather than on the POS screen because
   * this is where somebody knows the work is finished — and the API refuses
   * unless the job has actually passed the billing gate.
   */
  const bill = useMutation({
    mutationFn: () => invoiceFromJobCard(uid),
    onSuccess: (invoice) => {
      refresh();
      void navigate(`/pos/invoices/${invoice.uid}`);
    },
    onError: (e: unknown) => {
      fail(e, t('till.billFailed'));
    },
  });

  const qc = useMutation({
    mutationFn: (input: { itemUid: string; result: QcResult; note?: string }) =>
      recordQc(input.itemUid, {
        result: input.result,
        ...(input.note !== undefined ? { note: input.note } : {}),
      }),
    onSuccess: () => {
      refresh();
      setNoting(null);
    },
    onError: (e: unknown) => {
      fail(e, t('workshop.qcFailed'));
    },
  });

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  const job: JobCard = data;
  const closed = job.closedAt !== null;

  const recordQcResult = (item: JobQcItem, result: QcResult): void => {
    if (result === 'PASS') {
      qc.mutate({ itemUid: item.uid, result });
      return;
    }
    setQcNote(item.note ?? '');
    setNoting({ item, result });
  };

  return (
    <>
      <DetailPage
        title={orDash(job.plateNo)}
        subtitle={`${job.jobNo} · ${job.customerName}`}
        tags={[
          { label: JOB_STATUS_LABEL[job.status], colour: jobTone(job.status) },
          { label: JOB_TYPE_LABEL[job.jobType] },
          ...(job.isComeback
            ? [{ label: t('workshop.comebackOf', { jobNo: job.comebackOfJobNo ?? '' }), colour: 'error' }]
            : []),
          ...(job.isLate ? [{ label: t('workshop.late'), colour: 'error' }] : []),
        ]}
        onBack={() => {
          void navigate('/workshop/board');
        }}
        actions={
          closed ? null : (
            <Space wrap>
              <Button
                type="primary"
                onClick={() => {
                  setMileageOut(job.mileageOut);
                  setMoving(true);
                }}
              >
                {t('workshop.moveJob')}
              </Button>
              <Button
                onClick={() => {
                  setAmount(job.billableTotal);
                  setApprovedBy(job.customerName);
                  setApproving(true);
                }}
              >
                {t('workshop.recordApproval')}
              </Button>
              {job.status === 'COMPLETED' && (
                <Button
                  onClick={() => {
                    setClosing(true);
                  }}
                >
                  {t('workshop.closeJob')}
                </Button>
              )}
              {job.status === 'READY_FOR_BILLING' && (
                <Button
                  type="primary"
                  loading={bill.isPending}
                  onClick={() => {
                    bill.mutate();
                  }}
                >
                  {t('till.billThisJob')}
                </Button>
              )}
            </Space>
          )
        }
        tabs={[
          {
            key: 'work',
            label: t('workshop.tabs.work'),
            content: (
              <Flex vertical gap={16}>
                {job.billingBlockers.length > 0 && !closed && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('workshop.notReadyTitle')}
                    description={job.billingBlockers.join(' · ')}
                  />
                )}
                {job.isComeback && (
                  <Alert
                    type="error"
                    showIcon
                    message={t('workshop.comebackTitle', { jobNo: job.comebackOfJobNo ?? '' })}
                    description={t('workshop.comebackHelp')}
                  />
                )}

                <JobTechnicians job={job} onChanged={refresh} />

                <Card title={t('workshop.complaints')}>
                  <Timeline
                    items={job.complaints.map((complaint) => ({
                      children: (
                        <Flex vertical gap={4}>
                          <Text strong>{complaint.complaint}</Text>
                          {complaint.diagnosis !== null && (
                            <Text type="secondary">
                              {t('workshop.fields.diagnosis')}: {complaint.diagnosis}
                            </Text>
                          )}
                          {complaint.cause !== null && (
                            <Text type="secondary">
                              {t('workshop.fields.cause')}: {complaint.cause}
                            </Text>
                          )}
                          {complaint.correction !== null && (
                            <Text>
                              {t('workshop.fields.correction')}: {complaint.correction}
                            </Text>
                          )}
                          {complaint.technicianName !== null && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {complaint.technicianName}
                            </Text>
                          )}
                        </Flex>
                      ),
                    }))}
                  />
                </Card>

                <Card
                  title={t('workshop.lines')}
                  styles={{ body: { padding: 0 } }}
                  extra={<Text strong>{formatMoney(job.billableTotal)}</Text>}
                >
                  <Table<JobLine>
                    dataSource={job.lines}
                    rowKey={(row) => row.uid}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: t('workshop.noLines') }}
                    columns={[
                      {
                        title: t('workshop.fields.line'),
                        dataIndex: 'description',
                        render: (description: string, row: JobLine) => (
                          <Flex vertical gap={2}>
                            <Text>{description}</Text>
                            {row.productiveSeconds !== null && (
                              // The traceability that makes the number defensible:
                              // what the clock actually recorded.
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('workshop.clocked', {
                                  duration: describeSeconds(row.productiveSeconds),
                                })}
                              </Text>
                            )}
                            {row.adjustmentReason !== null && (
                              <Text type="warning" style={{ fontSize: 12 }}>
                                {row.adjustmentReason}
                              </Text>
                            )}
                          </Flex>
                        ),
                      },
                      {
                        title: t('workshop.fields.qty'),
                        dataIndex: 'qty',
                        width: 90,
                        align: 'right' as const,
                      },
                      {
                        title: t('workshop.fields.rate'),
                        dataIndex: 'unitPrice',
                        width: 130,
                        align: 'right' as const,
                        render: (value: string) => formatMoney(value),
                      },
                      {
                        title: t('sales.fields.lineTotal'),
                        dataIndex: 'lineTotal',
                        width: 140,
                        align: 'right' as const,
                        render: (value: string, row: JobLine) =>
                          row.isBillable ? (
                            <Text strong>{formatMoney(value)}</Text>
                          ) : (
                            // Warranty and insurance: the work is recorded, the
                            // customer pays nothing.
                            <Text type="secondary">{t('workshop.notBilled')}</Text>
                          ),
                      },
                      {
                        title: '',
                        width: 90,
                        render: (_: unknown, row: JobLine) =>
                          row.type === 'LABOUR' && !closed ? (
                            <Button
                              size="small"
                              onClick={() => {
                                setHours(row.qty);
                                setAdjustReason('');
                                setAdjusting(row);
                              }}
                            >
                              {t('workshop.adjust')}
                            </Button>
                          ) : null,
                      },
                    ]}
                  />
                </Card>

                <JobParts job={job} />
              </Flex>
            ),
          },
          {
            key: 'qc',
            label: t('workshop.tabs.qc'),
            content: (
              <Card styles={{ body: { padding: 0 } }}>
                <Table<JobQcItem>
                  dataSource={job.qcItems}
                  rowKey={(row) => row.uid}
                  pagination={false}
                  size="middle"
                  scroll={{ x: 'max-content' }}
                  columns={[
                    {
                      title: t('workshop.fields.check'),
                      dataIndex: 'label',
                      render: (label: string, row: JobQcItem) => (
                        <Flex vertical gap={2}>
                          <Text>{label}</Text>
                          {row.note !== null && (
                            <Text
                              type={row.result === 'FAIL' ? 'danger' : 'secondary'}
                              style={{ fontSize: 12 }}
                            >
                              {row.note}
                            </Text>
                          )}
                        </Flex>
                      ),
                    },
                    {
                      title: t('delivery.fields.result'),
                      dataIndex: 'result',
                      width: 260,
                      render: (result: QcResult | null, row: JobQcItem) =>
                        closed ? (
                          result === null ? (
                            <Text type="secondary">—</Text>
                          ) : (
                            <Tag>{QC_RESULT_LABEL[result]}</Tag>
                          )
                        ) : (
                          <Radio.Group
                            value={result}
                            size="large"
                            optionType="button"
                            buttonStyle="solid"
                            onChange={(e) => {
                              recordQcResult(row, e.target.value as QcResult);
                            }}
                          >
                            <Radio.Button value="PASS">
                              <CheckOutlined /> {QC_RESULT_LABEL.PASS}
                            </Radio.Button>
                            <Radio.Button value="FAIL">
                              <CloseOutlined /> {QC_RESULT_LABEL.FAIL}
                            </Radio.Button>
                            <Radio.Button value="NA">
                              <MinusOutlined /> {QC_RESULT_LABEL.NA}
                            </Radio.Button>
                          </Radio.Group>
                        ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'approvals',
            label: t('workshop.tabs.approvals'),
            content: (
              <Flex vertical gap={16}>
                <Alert
                  type="info"
                  showIcon
                  message={t('workshop.approvedCeiling', {
                    amount:
                      job.approvedAmount === null
                        ? t('workshop.nothingApproved')
                        : formatMoney(job.approvedAmount),
                  })}
                  description={t('workshop.ceilingHelp')}
                />
                <Card styles={{ body: { padding: 0 } }}>
                  <Table
                    dataSource={approvals ?? []}
                    rowKey={(row) => row.uid}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: t('workshop.noApprovals') }}
                    columns={[
                      {
                        title: t('workshop.fields.approvedBy'),
                        dataIndex: 'approvedByName',
                      },
                      {
                        title: t('workshop.fields.channel'),
                        dataIndex: 'channel',
                        width: 150,
                        render: (value: string) => t(`workshop.channel.${value}`),
                      },
                      {
                        title: t('workshop.fields.from'),
                        dataIndex: 'amountBefore',
                        width: 130,
                        align: 'right' as const,
                        render: (value: string) => formatMoney(value),
                      },
                      {
                        title: t('workshop.fields.to'),
                        dataIndex: 'amountAfter',
                        width: 130,
                        align: 'right' as const,
                        render: (value: string) => <Text strong>{formatMoney(value)}</Text>,
                      },
                      {
                        title: t('workshop.fields.when'),
                        dataIndex: 'approvedAt',
                        width: 180,
                        render: (value: string) => formatDateTime(value),
                      },
                    ]}
                  />
                </Card>
              </Flex>
            ),
          },
          {
            key: 'overview',
            label: t('sales.tabs.overview'),
            content: (
              <Card>
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                  <Descriptions.Item label={t('vehicles.fields.plateNo')}>
                    {orDash(job.plateNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.chassisNo')}>
                    {job.chassisNo}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.customer')}>{job.customerName}</Descriptions.Item>
                  <Descriptions.Item label={t('fields.phone')}>{orDash(job.customerPhone)}</Descriptions.Item>
                  <Descriptions.Item label={t('workshop.fields.mileageIn')}>
                    {job.mileageIn === null ? '—' : `${String(job.mileageIn)} km`}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('workshop.fields.mileageOut')}>
                    {job.mileageOut === null ? '—' : `${String(job.mileageOut)} km`}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('workshop.fields.arrived')}>
                    {formatDateTime(job.arrivedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('workshop.fields.promised')}>
                    <Text type={job.isLate ? 'danger' : undefined}>{formatDateTime(job.promisedAt)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('workshop.fields.advisor')}>
                    {orDash(job.serviceAdvisorName)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('workshop.fields.technicians')}>
                    {job.technicians.length === 0
                      ? t('workshop.nobody')
                      : job.technicians.map((tech) => tech.name).join(', ')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
        ]}
      />

      <ActionDrawer
        open={moving}
        title={t('workshop.moveJob')}
        submitLabel={t('workshop.moveJob')}
        submitting={move.isPending}
        onClose={() => {
          setMoving(false);
        }}
        onSubmit={() => {
          move.mutate();
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t('workshop.fields.moveTo')} required>
            <Select
              size="large"
              value={nextStatus}
              onChange={setNextStatus}
              options={ADVISOR_STATUSES.map((s) => ({ value: s, label: JOB_STATUS_LABEL[s] }))}
            />
          </Form.Item>
          {(nextStatus === 'COMPLETED' || nextStatus === 'READY_FOR_COLLECTION') && (
            <Form.Item label={t('workshop.fields.mileageOut')} help={t('workshop.mileageOutHelp')}>
              <InputNumber
                min={0}
                max={999999}
                size="large"
                style={{ width: '100%' }}
                value={mileageOut}
                onChange={setMileageOut}
                suffix="km"
              />
            </Form.Item>
          )}
          {nextStatus === 'READY_FOR_BILLING' && job.billingBlockers.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('workshop.willBeRefused')}
              description={job.billingBlockers.join(' · ')}
            />
          )}
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={closing}
        title={t('workshop.closeJob')}
        submitLabel={t('workshop.closeJob')}
        submitting={close.isPending}
        danger
        onClose={() => {
          setClosing(false);
        }}
        onSubmit={() => {
          close.mutate();
        }}
        consequence={t('workshop.closeConsequence')}
      >
        <Text>{t('workshop.closeQuestion', { jobNo: job.jobNo })}</Text>
      </ActionDrawer>

      <ActionDrawer
        open={approving}
        title={t('workshop.recordApproval')}
        submitLabel={t('workshop.recordApproval')}
        submitting={approve.isPending}
        submitDisabled={amount.trim().length === 0 || approvedBy.trim().length < 2}
        onClose={() => {
          setApproving(false);
        }}
        onSubmit={() => {
          approve.mutate();
        }}
        consequence={t('workshop.approvalConsequence')}
      >
        <Form layout="vertical">
          <Form.Item label={t('workshop.fields.amountApproved')} required>
            <Input
              inputMode="decimal"
              prefix="MYR"
              size="large"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
              }}
            />
          </Form.Item>
          <Form.Item label={t('workshop.fields.channel')} required help={t('workshop.channelHelp')}>
            <Radio.Group
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value as ApprovalChannel);
              }}
              size="large"
              buttonStyle="solid"
              optionType="button"
              options={CHANNELS.map((c) => ({ value: c, label: t(`workshop.channel.${c}`) }))}
            />
          </Form.Item>
          <Form.Item label={t('workshop.fields.approvedBy')} required>
            <Input
              size="large"
              value={approvedBy}
              onChange={(e) => {
                setApprovedBy(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={adjusting !== null}
        title={t('workshop.adjustTitle')}
        submitLabel={t('common.save')}
        submitting={adjust.isPending}
        submitDisabled={adjustReason.trim().length < 3}
        onClose={() => {
          setAdjusting(null);
        }}
        onSubmit={() => {
          adjust.mutate();
        }}
      >
        <Form layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('workshop.clockedTotal', {
              duration: describeSeconds(adjusting?.productiveSeconds ?? 0),
              hours: adjusting?.qty ?? '0',
            })}
            // Down only. Charging above what the clock recorded is a new
            // customer approval, not an adjustment.
            description={t('workshop.adjustDownOnly')}
          />
          <Form.Item label={t('workshop.fields.billHours')} required>
            <Input
              inputMode="decimal"
              size="large"
              value={hours}
              onChange={(e) => {
                setHours(e.target.value);
              }}
              suffix={t('workshop.hoursUnit')}
            />
          </Form.Item>
          <Form.Item label={t('workshop.fields.why')} required>
            <Input.TextArea
              rows={3}
              value={adjustReason}
              onChange={(e) => {
                setAdjustReason(e.target.value);
              }}
              placeholder={t('workshop.adjustPlaceholder')}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={noting !== null}
        title={noting?.item.label ?? ''}
        submitLabel={t('common.save')}
        submitting={qc.isPending}
        submitDisabled={qcNote.trim().length < 3}
        onClose={() => {
          setNoting(null);
        }}
        onSubmit={() => {
          if (noting === null) return;
          qc.mutate({ itemUid: noting.item.uid, result: noting.result, note: qcNote.trim() });
        }}
      >
        <Form layout="vertical">
          <Form.Item
            label={
              noting?.result === 'FAIL'
                ? t('delivery.fields.whatIsWrong')
                : t('delivery.fields.whyNotApplicable')
            }
            required
            help={t('workshop.qcNoteHelp')}
          >
            <Input.TextArea
              rows={3}
              value={qcNote}
              onChange={(e) => {
                setQcNote(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    </>
  );
}
